import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { PaletteMode } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { createMuiTheme } from '../theme/mui-theme';

// Same key the inline script in public/index.html reads to avoid a flash of the wrong theme
const STORAGE_KEY = 'pb-theme';
const darkQuery = '(prefers-color-scheme: dark)';

interface ThemeModeValue {
    mode: PaletteMode,
    toggleMode: () => void,
}

const ThemeModeContext = createContext<ThemeModeValue>({
    mode: 'light',
    toggleMode: () => { }
});

const readStoredMode = (): PaletteMode | undefined => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored === 'light' || stored === 'dark' ? stored : undefined;
    } catch {
        return undefined;
    }
}

const systemMode = (): PaletteMode => window.matchMedia?.(darkQuery).matches ? 'dark' : 'light';

export function ThemeModeProvider({ children }: { children: ReactNode }) {
    const [chosen, setChosen] = useState<PaletteMode | undefined>(readStoredMode);
    const [system, setSystem] = useState<PaletteMode>(systemMode);

    useEffect(() => {
        const query = window.matchMedia?.(darkQuery);
        if (!query) return;

        const onChange = (e: MediaQueryListEvent) => setSystem(e.matches ? 'dark' : 'light');

        query.addEventListener('change', onChange);
        return () => query.removeEventListener('change', onChange);
    }, []);

    useEffect(() => {
        if (chosen)
            document.documentElement.dataset.theme = chosen;
        else
            delete document.documentElement.dataset.theme;
    }, [chosen]);

    const mode = chosen ?? system;

    const toggleMode = useCallback(() => {
        const next: PaletteMode = mode === 'dark' ? 'light' : 'dark';

        setChosen(next);
        try {
            localStorage.setItem(STORAGE_KEY, next);
        } catch { }
    }, [mode]);

    const muiTheme = useMemo(() => createMuiTheme(mode), [mode]);
    const value = useMemo(() => ({ mode, toggleMode }), [mode, toggleMode]);

    return <ThemeModeContext.Provider value={value}>
        <ThemeProvider theme={muiTheme}>
            {children}
        </ThemeProvider>
    </ThemeModeContext.Provider>
}

export const useThemeMode = () => useContext(ThemeModeContext);
